/**
 * /academy/progress for German (D-118): the figures of ONE Goethe-Zertifikat level (the active
 * one, or the one chosen here): the verdict, the critical points first, the points of each module
 * on the official scale against the learner's target, the evolution of the sittings of the level,
 * the rank among the learners of the same level (positions only) and the PDF report. Estimates.
 */

import { useAcademyProgressRetrieve } from "@agencyos/api-client";
import { GOETHE_MODULES, type GoetheLevel, type GoetheSkill, formatPoints, parseGoetheProgress } from "@agencyos/core";
import { Badge, GlassCard, SegmentedControl, Skeleton } from "@agencyos/ui-web";
import { AlertTriangle, TrendingUp, Trophy } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReportButton } from "../ProgressPage";
import { RankScoreCard } from "../RankScoreCard";
import { dateText } from "../shared";
import { isObject } from "../types";
import { CardTitle, PageHead, SectionTitle, SkillIcon } from "../ui";
import { GOETHE, useGoethe, useGoetheSave } from "./useGoethe";
import "./goethe.css";

const SKILLS: GoetheSkill[] = ["co", "ce", "ee", "eo"];

// D-123: the reading is shared with the back office and the phone (@agencyos/core)
const read = parseGoetheProgress;

const VERDICT_TONE: Record<string, "success" | "warning" | "danger"> = { ready: "success", almost: "warning", not_ready: "danger" };

export function GoetheProgressPage() {
  const { t, i18n } = useTranslation();
  const { state } = useGoethe();
  const save = useGoetheSave();
  const prepared = state?.levels.map((l) => l.level) ?? [];
  const [chosen, setChosen] = useState<GoetheLevel | "">("");
  const level = chosen || state?.activeLevel || "";
  const query = useAcademyProgressRetrieve({ exam: GOETHE, ...(level ? { level } : {}) }, { query: { retry: false, enabled: Boolean(level) } });
  const data = useMemo(() => read(query.data?.status === 200 ? query.data.data : null), [query.data]);
  if (!state || (level && query.isLoading)) return <Skeleton style={{ height: 320 }} />;
  if (!level) return <p className="ac-note">{t("goethe.progress.noLevel")}</p>;

  return (
    <div className="ac-page ac-goethe">
      <PageHead eyebrow={`${t("ielts.exams.goethe.name")} · ${t("academy.progress.eyebrow")}`} title={t("goethe.progress.title", { level })} subtitle={<p className="ac-note">{t("goethe.estimateLong")}</p>} />
      {prepared.length > 1 && (
        <SegmentedControl
          label={t("goethe.levels.title")}
          value={level}
          options={prepared.map((l) => ({ value: l, label: l }))}
          onChange={(value) => {
            setChosen(value as GoetheLevel);
            save.mutate({ data: { level: value as GoetheLevel } });
          }}
        />
      )}
      <GlassCard as="section" className="ac-card" aria-labelledby="goethe-verdict">
        <CardTitle id="goethe-verdict" icon={<TrendingUp size={16} />} tone="info">
          {t(`goethe.progress.verdict.${data.verdict}`)}
        </CardTitle>
        <Badge tone={VERDICT_TONE[data.verdict] ?? "danger"}>{t("goethe.goal.targetValue", { n: data.target ?? 60 })}</Badge>
        {data.weeks ? <p className="ac-caption">{t("goethe.progress.weeks", { count: data.weeks })}</p> : null}
        {data.critical.length > 0 && (
          <ol className="ac-critical">
            {data.critical.map((message, index) => (
              <li key={index}>
                <AlertTriangle aria-hidden size={14} /> {message}
              </li>
            ))}
          </ol>
        )}
      </GlassCard>

      <section className="ac-section" aria-labelledby="goethe-modules">
        <SectionTitle id="goethe-modules">{t("goethe.home.levels", { level })}</SectionTitle>
        <ul className="ac-tef-levels">
          {SKILLS.map((skill) => {
            const row = data.rows[skill];
            return (
              <li key={skill} className={`ac-tef-level ac-surface ac-skill--${skill}`}>
                <SkillIcon skill={skill} size={16} />
                <span className="ac-tef-level__name">{GOETHE_MODULES[skill]}</span>
                <span className="ac-tef-level__value ao-num">{row.points !== null ? `${formatPoints(row.points, i18n.language)} / ${formatPoints(row.max, i18n.language)}` : "—"}</span>
                <span className="ac-caption">{row.rating}</span>
                {row.gap && row.gap > 0 ? <span className="ac-caption ao-num">{t("goethe.progress.gap", { n: formatPoints(row.gap, i18n.language) })}</span> : null}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="ac-section" aria-labelledby="goethe-evolution">
        <SectionTitle id="goethe-evolution">{t("goethe.progress.evolution")}</SectionTitle>
        {data.evolution.length ? (
          <div className="ac-table-wrap">
            <table className="ac-criteria">
              <thead>
                <tr>
                  <th scope="col">{t("goethe.progress.date")}</th>
                  <th scope="col">{t("goethe.progress.sitting")}</th>
                  {SKILLS.map((skill) => (
                    <th key={skill} scope="col" className="num">
                      {GOETHE_MODULES[skill]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.evolution.map((row, index) => (
                  <tr key={index}>
                    <td>{row.at ? dateText(row.at, i18n.language) : "—"}</td>
                    <td>
                      {t(`academy.kind.${row.kind}`)}
                      {row.mock !== null ? ` n° ${row.mock}` : ""}
                    </td>
                    {SKILLS.map((skill) => {
                      const cell = isObject(row.modules[skill]) ? (row.modules[skill] as Record<string, unknown>) : null;
                      return (
                        <td key={skill} className="num ao-num">
                          {cell && typeof cell.percent === "number" ? `${formatPoints(cell.percent, i18n.language)} %` : "—"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="ac-secondary">{t("goethe.progress.none")}</p>
        )}
      </section>
      {data.rank && (
        <GlassCard as="section" className="ac-card" aria-labelledby="goethe-rank">
          <CardTitle id="goethe-rank" icon={<Trophy size={16} />} tone="warning">
            {t("goethe.progress.rank", { level, position: data.rank.position, total: data.rank.total, top: data.rank.top })}
          </CardTitle>
        </GlassCard>
      )}
      <RankScoreCard id="goethe-rank-score" rank={query.data?.status === 200 ? query.data.data.rank : null} />
      <ReportButton exam="goethe" />
    </div>
  );
}
